import { baseApi } from "../../../services/rtkApi/baseApi";
import { aotdPaths as P } from "./apiPaths";
import { quizPaths } from "./quizTypes";
import type { GetQuizResponse, PostQuizGuessBody, PostQuizGuessResponse, PutQuizPrizeBody, QuizBoardDto } from "./quizTypes";
import type {
  EmojiPenaltyResponse,
  MyAotdStats,
  RaceAuditGetResponse,
  RaceAuditPostResponse,
  StatusResponse,
  TodayResponse,
} from "./types";

/**
 * AOTD RTK Query slice. Injected into `baseApi` so it shares the auth headers
 * and reauth wrapper used by the rest of the SPA.
 *
 * Invariant: `enterTodayRace` is the canonical hit on `/today` for the race
 * window. It is a **mutation** (not a query) because:
 *   1. The state machine must capture `X-Client-Sent-At` at the moment of
 *      call, with no caching, dedup, or refetch — RTK queries would interpose
 *      a cache layer.
 *   2. The response can be `race_lost` / `entered_early`, which we still want
 *      to surface to the UI; `validateStatus: () => true` keeps non-2xx
 *      bodies out of the `error` branch.
 *
 * The blob URL returned by `getTodayImage` is **not** owned by RTK Query
 * (`keepUnusedDataFor: 0`). The orchestrator hook (phase 1) is responsible
 * for revoking it when superseded.
 */

/**
 * How a race request was sent (telemetry on the race row, never ranks): the
 * race button, any other press (footer refresh, retry, generate), or the page
 * firing by itself (load, no-art retry, a signed-out viewer following the day).
 */
export type RaceTapPath = "button" | "refresh" | "auto";

interface EnterTodayRaceArgs {
  /** Observed round-trip ping (EMA) captured at call time. */
  pingMs?: number;
  /** ISO-8601 timestamp captured at call site (`new Date().toISOString()`). */
  clientSentAt: string;
  /** The race button, another press, or the page firing by itself. */
  tapPath?: RaceTapPath;
  /** How far this device's clock runs ahead of the server's (clock sync). */
  clockAheadMs?: number | null;
  /** ms between the finger landing on the race button and this call. */
  tapDelayMs?: number;
}

/** `GET /today` answered before any race work (backend `raceWarmup`). */
export interface RaceWarmupResponse {
  warmup: boolean;
  serverNow: number;
}

interface SetQuoteArgs {
  quote: string;
}

interface SuggestQuoteArgs {
  quote: string;
}

interface SetNextEmojisArgs {
  emojis: string[];
}

export const artOfTheDayApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    enterTodayRace: builder.mutation<TodayResponse, EnterTodayRaceArgs>({
      // The URL carries no query string on purpose: browsers cache the CORS
      // preflight per URL, and `raceWarmup` below pre-caches it for exactly
      // this URL. Everything variable rides in headers.
      query: ({ clientSentAt, pingMs, tapPath, clockAheadMs, tapDelayMs }) => ({
        url: P.today,
        method: "GET",
        headers: {
          "X-Client-Sent-At": String(clientSentAt),
          ...(typeof pingMs === "number" ? { "X-Client-Ping-Ms": String(Math.round(pingMs)) } : {}),
          ...(tapPath ? { "X-Race-Tap": tapPath } : {}),
          ...(typeof clockAheadMs === "number"
            ? { "X-Client-Clock-Ahead": String(Math.round(clockAheadMs)) }
            : {}),
          ...(typeof tapDelayMs === "number"
            ? { "X-Race-Tap-Delay": String(Math.max(0, Math.round(tapDelayMs))) }
            : {}),
        },
        validateStatus: () => true,
      }),
      invalidatesTags: ["AotdToday", "AotdStatus", "AotdEmojiPenalty"],
    }),
    /**
     * Pre-unlock warm-up: same URL and transport as `enterTodayRace`, so the
     * CORS preflight and the connection the tap needs are already there, and
     * `serverNow` syncs the countdown. The backend answers it before auth and
     * never treats it as a race entry.
     */
    raceWarmup: builder.mutation<RaceWarmupResponse, void>({
      query: () => ({
        url: P.today,
        method: "GET",
        headers: { "X-Race-Warmup": "1" },
      }),
    }),
    getStatus: builder.query<StatusResponse, void>({
      query: () => P.status,
      providesTags: ["AotdStatus"],
    }),
    getInfo: builder.query<StatusResponse, void>({
      query: () => P.info,
      providesTags: ["AotdInfo"],
    }),
    getRaceState: builder.query<unknown, void>({
      query: () => P.raceState,
      providesTags: ["AotdRaceState"],
    }),
    getTodayImage: builder.query<string, void>({
      query: () => ({
        url: P.todayImage,
        responseHandler: async (response: Response) =>
          URL.createObjectURL(await response.blob()),
        cache: "no-store",
      }),
      keepUnusedDataFor: 0,
    }),
    setQuote: builder.mutation<unknown, SetQuoteArgs>({
      query: (body) => ({
        url: P.quote,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AotdInfo"],
    }),
    suggestQuote: builder.mutation<unknown, SuggestQuoteArgs>({
      query: (body) => ({
        url: P.suggestQuote,
        method: "POST",
        body,
      }),
    }),
    setNextEmojis: builder.mutation<unknown, SetNextEmojisArgs>({
      query: (body) => ({
        url: P.setNextEmojis,
        method: "POST",
        body,
      }),
      // EmojiSelector: the registry selector/keyboard carries per-user
      // staged flags that change on every successful set
      invalidatesTags: ["AotdEmojis", "AotdInfo", "EmojiSelector", "AotdEmojiPenalty"],
    }),
    /** The viewer's side of the no-emoji penalty (warning before the tap, result after). */
    getEmojiPenalty: builder.query<EmojiPenaltyResponse, void>({
      query: () => P.emojiPenalty,
      providesTags: ["AotdEmojiPenalty"],
    }),
    /** Comment count only. Signed-out visitors get `total` but no comments. */
    getCommentCount: builder.query<number, string>({
      query: (aotdId) => ({
        url: `${P.comments}/${aotdId}`,
        params: { limit: 1 },
      }),
      transformResponse: (res: { data?: { total?: number } }) =>
        typeof res?.data?.total === "number" ? res.data.total : 0,
    }),
    getUsedEmojis: builder.query<unknown, void>({
      query: () => P.usedEmojis,
      providesTags: ["AotdEmojis"],
    }),
    triggerWayback: builder.mutation<unknown, void>({
      query: () => ({
        url: P.waybackTrigger,
        method: "POST",
      }),
      invalidatesTags: ["AotdInfo"],
    }),
    retryWaybackAdmin: builder.mutation<unknown, string>({
      query: (id) => ({
        url: `/art-of-the-day/admin/${id}/wayback/retry`,
        method: "POST",
      }),
    }),
    /** Cached Race Auditor result for today + whether the caller may dispute. */
    getRaceAudit: builder.query<RaceAuditGetResponse, void>({
      query: () => P.audit,
      providesTags: ["AotdRaceAudit"],
    }),
    /** The caller's own AOTD race numbers, for the profile "aotd stats" box. */
    getMyAotdStats: builder.query<MyAotdStats, void>({
      query: () => P.myStats,
    }),
    /**
     * Today's emoji quiz board for this viewer (null until the art is
     * published, or on a day without emojis). Signed-out callers get 200 with
     * `you.signedIn: false`. Live updates arrive over the race socket
     * (`quizSocket.ts`), which patches or invalidates this entry.
     */
    getQuiz: builder.query<GetQuizResponse, void>({
      query: () => quizPaths.board,
      providesTags: ["AotdQuiz"],
    }),
    /**
     * Lock a guess. The response carries the viewer's board after the lock
     * (closed on the spot when it was a non-author perfect), so the cache is
     * written from it instead of refetching.
     */
    postQuizGuess: builder.mutation<PostQuizGuessResponse, PostQuizGuessBody>({
      query: (body) => ({
        url: quizPaths.guess,
        method: "POST",
        body,
      }),
      async onQueryStarted(_body, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            artOfTheDayApi.util.updateQueryData("getQuiz", undefined, (draft) => {
              draft.quiz = data.quiz;
            }),
          );
        } catch {
          // the box shows the error; the cached board stays as it was
        }
      },
    }),
    /** Admin: set the card today's winner will get. Answers with the board. */
    putQuizPrize: builder.mutation<{ success: true; quiz: QuizBoardDto }, PutQuizPrizeBody>({
      query: (body) => ({
        url: quizPaths.prize,
        method: "PUT",
        body,
      }),
      async onQueryStarted(_body, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            artOfTheDayApi.util.updateQueryData("getQuiz", undefined, (draft) => {
              draft.quiz = data.quiz;
            }),
          );
        } catch {
          // the picker shows the error
        }
      },
    }),
    /** Run (or return cached) audit for today. `refresh` forces a re-run (admin only). */
    auditRace: builder.mutation<RaceAuditPostResponse, { refresh?: boolean } | void>({
      query: (args) => ({
        url: P.audit,
        method: "POST",
        params: args && args.refresh ? { refresh: "1" } : undefined,
      }),
      invalidatesTags: ["AotdRaceAudit"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useEnterTodayRaceMutation,
  useRaceWarmupMutation,
  useGetStatusQuery,
  useGetInfoQuery,
  useGetRaceStateQuery,
  useGetTodayImageQuery,
  useSetQuoteMutation,
  useSuggestQuoteMutation,
  useSetNextEmojisMutation,
  useGetUsedEmojisQuery,
  useTriggerWaybackMutation,
  useRetryWaybackAdminMutation,
  useGetRaceAuditQuery,
  useAuditRaceMutation,
  useGetMyAotdStatsQuery,
  useGetEmojiPenaltyQuery,
  useGetCommentCountQuery,
  useGetQuizQuery,
  usePostQuizGuessMutation,
  usePutQuizPrizeMutation,
} = artOfTheDayApi;
